/**
 * Date helpers for the export.
 *
 * ⚠️ Text only. Nothing here builds a local `Date` from the input, because
 * `new Date('2000-06-17T00:00:00.000Z')` shows 16 June in any time zone behind UTC.
 * We read the digits and never shift them.
 */

export type DateIssue = 'unreadable' | 'invalid';

export interface ParsedDate {
  /** Plain `yyyy-MM-dd`, or null when the input is empty / unreadable / invalid. */
  value: string | null;
  /** Why value is null. Null when the input was simply empty. */
  issue: DateIssue | null;
}

const EMPTY: ParsedDate = { value: null, issue: null };
const MIN_YEAR = 1900;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n: number) => String(n).padStart(2, '0');
const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysIn = (y: number, m: number) =>
  [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

/** Pulls [year, month, day] out of the formats we accept, or null if none match. */
function readParts(text: string): [number, number, number] | null {
  let hit: RegExpMatchArray | null;

  // 2000-06-17 / 2000-06-17T00:00:00.000Z / 2000-06-17 00:00:00 (time part ignored)
  if ((hit = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/))) {
    return [Number(hit[1]), Number(hit[2]), Number(hit[3])];
  }
  // 20000617 / 20000617000000 (the app's compact SDK timestamps)
  if ((hit = text.match(/^(\d{4})(\d{2})(\d{2})(?:\d{6})?$/))) {
    return [Number(hit[1]), Number(hit[2]), Number(hit[3])];
  }
  // 2000/06/17
  if ((hit = text.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/))) {
    return [Number(hit[1]), Number(hit[2]), Number(hit[3])];
  }
  // 17/06/2000, 17-06-2000, 17.06.2000 — DAY first (South African style).
  if ((hit = text.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/))) {
    return [Number(hit[3]), Number(hit[2]), Number(hit[1])];
  }
  return null;
}

/**
 * Any date-of-birth input → clean `yyyy-MM-dd`.
 *
 * Accepts ISO text (with or without a time part), compact `yyyyMMdd[HHmmss]`, `yyyy/MM/dd` and
 * day-first `dd/MM/yyyy`. Anything else is reported as 'unreadable' instead of being guessed; a real
 * calendar date that is implausible (before 1900, in the future, 0001-01-01, 0) is 'invalid'.
 */
export function parseDateOfBirth(raw: unknown): ParsedDate {
  if (raw === null || raw === undefined) return EMPTY;
  const text = String(raw).trim();
  if (text === '') return EMPTY;
  if (/^0+$/.test(text)) return { value: null, issue: 'invalid' }; // numeric 0 sentinel

  const parts = readParts(text);
  if (!parts) return { value: null, issue: 'unreadable' };
  const [y, m, d] = parts;

  // Year first, so 0001-01-01 is rejected before any calendar maths.
  if (y < MIN_YEAR || m < 1 || m > 12 || d < 1 || d > daysIn(y, m)) {
    return { value: null, issue: 'invalid' };
  }
  const now = new Date();
  const today = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  if (y * 10000 + m * 100 + d > today) return { value: null, issue: 'invalid' };

  return { value: `${y}-${pad(m)}-${pad(d)}`, issue: null };
}

/** `2000-06-17T00:00:00.000Z` → `17 Jun 2000`. Empty string when it cannot be read. */
export function formatDateNice(raw: unknown): string {
  const { value } = parseDateOfBirth(raw);
  if (!value) return '';
  const [y, m, d] = value.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}
